import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../config';

// Mengambil domain saat ini (murni dinamis sesuai website yang diakses)
const getDomain = () => {
  // Menggunakan origin agar mencakup http:// atau https:// beserta nama domainnya,
  // lalu ditambahkan '/' di belakangnya sesuai dengan format yang diminta API.
  // Contoh hasil: "https://desapedialbb.okukab.go.id/"
  return window.location.origin + "/";
};

const buildImageUrl = (path) =>
  path ? `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(path.trim())}` : '';

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: config.BASE_URL }),
  endpoints: (builder) => ({
    getOrganizationContent: builder.query({
      query: (domain = getDomain()) => `organization/findContentOrganization?namaDomain=${encodeURIComponent(domain)}`,
      transformResponse: (response) => {
        if (!response?.status) {
          throw new Error('Failed to load data');
        }

        const {
          berita, organization, dusun, carousel,
          aparaturOrganizations, struktural,
          fasilitas, komoditas, kegiatan, layanan,
          infografis, publikasi, galeriDesaCantik,
          dokumentasiDesaCantik,
        } = response.data || {};

        const articles = (berita || []).map((item) => ({
          beritaId: item.beritaId,
          judul: item.judul || 'Untitled Article',
          isi: item.isi || '-',
          created: item.tanggalRelease || item.created,
          foto: buildImageUrl(item.foto),
        }));

        const aparaturData = (aparaturOrganizations || []).map((item) => ({
          img: buildImageUrl(item.foto),
          nama: item.nama,
          jabatan: item.jabatan,
          quotes: item.quotes,
        }));

        const transformedKomoditas = (komoditas || []).map((item, index) => ({
          komoditasId: item.komoditasId || index,
          created: item.created || '',
          nama: item.nama || 'Unknown Name',
          keterangan: item.keterangan || 'No Description',
          foto: buildImageUrl(item.foto),
        }));

        let strukturDesa = '';
        if (Array.isArray(struktural) && struktural[0]?.foto) {
          strukturDesa = buildImageUrl(struktural[0].foto);
        }

        const formattedFasilitas = (fasilitas || []).map((item) => ({
          ...item,
          foto: (item.fasilitasDetail || []).map((d) => buildImageUrl(d.foto)),
        }));

        const formattedKegiatan = (kegiatan || []).map((item) => ({
          ...item,
          foto: (item.kegiatanDetail || []).map((d) => buildImageUrl(d.foto)),
        }));

        const formattedLayanan = (layanan || []).map((item) => ({
          nama: item.nama || '',
          keterangan: item.keterangan || 'Deskripsi tidak tersedia',
          created: item.created,
          createdBy: item.createdBy,
          layananId: item.layananId,
        }));

        const footerData = {
          alamat: organization?.alamat || '',
          contactNumber: organization?.contactNumber || '',
          email: organization?.email || '',
          logo: buildImageUrl(organization?.logo),
        };

        return {
          nama: organization?.nama || '',
          organizationId: organization?.organizationId || null,
          selamatDatangDesaCantik: organization?.selamatDatangDesaCantik || '',
          visi: organization?.visi || '',
          misi: organization?.misi || '',
          videoUrl: organization?.urlVideo
            ? `https://www.youtube.com/embed/${organization.urlVideo}?autoplay=1`
            : '',
          petaUrl: organization?.peta || '',
          articles,
          footerData,
          dataDusun: (dusun || []).map((item, index) => ({
            nama: item.nama || `Dusun ${index + 1}`,
            jumlah: item.jumlahWarga || '-',
            created: item.created,
            createdBy: item.createdBy,
            mdusunId: item.mdusunId,
          })),
          carouselItems: (carousel || []).map((item) => ({
            carouselId: item.carouselId,
            deskripsi: item.deskripsi,
            image: (item.image && item.image !== 'null' && item.image.trim() !== '')
              ? buildImageUrl(item.image)
              : '',
            backgroundImage: (item.backgroundImage && item.backgroundImage !== 'null' && item.backgroundImage.trim() !== '')
              ? buildImageUrl(item.backgroundImage)
              : '',
            width: item.width,
            height: item.height,
            positionVertical: item.positionVertical,
            positionHorizontal: item.positionHorizontal,
          })),
          aparaturData,
          strukturDesa,
          fasilitas: formattedFasilitas,
          komoditas: transformedKomoditas,
          kegiatan: formattedKegiatan,
          layanan: formattedLayanan,
          infografis: infografis || [],
          publikasi: publikasi || [],
          galeriDesaCantik: galeriDesaCantik || [],
          dokumentasiDesaCantik: dokumentasiDesaCantik || [],
        };
      }
    }),
    getDesaCantikDashboard: builder.query({
      query: ({ organizationId, bulan, tahun }) =>
        `DesaCantik/dashboard/findOne/${organizationId}?bulan=${bulan}&tahun=${tahun}`,
      transformResponse: (response) => {
        return response?.data || null;
      }
    }),
    getReleasedDashboards: builder.query({
      queryFn: async (organizationId) => {
        try {
          const url = `${config.BASE_URL_FORM_DINAMIS}api/dashboards/released/${organizationId}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error('Network response was not ok');
          const json = await res.json();
          let dashboards = json?.data || [];
          // Sort dari yang paling lama (oldest first)
          dashboards.sort((a, b) => new Date(a.createdAt || a.lastReleasedAt || 0) - new Date(b.createdAt || b.lastReleasedAt || 0));
          return { data: dashboards };
        } catch (error) {
          console.error("Error fetching dynamic dashboards:", error);
          return { error: error.message };
        }
      }
    }),
  }),
});

export const { useGetOrganizationContentQuery, useGetDesaCantikDashboardQuery, useGetReleasedDashboardsQuery } = apiSlice;
